// frontend/src/components/layout/FeedbackWidget.jsx
import React, { useState, useRef, useCallback } from 'react';
import { MessageSquare, X, Paperclip, Check, Loader2 } from 'lucide-react';
import api from '../../utils/api';

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const fileRef = useRef(null);

  const resetForm = useCallback(() => {
    setType('bug');
    setMessage('');
    setFile(null);
    setPreview(null);
    setError('');
  }, []);

  const handleOpen = () => {
    if (cooldown) return;
    setOpen(true);
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    if (success) resetForm();
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 5 * 1024 * 1024) {
      setError('Screenshot must be under 5MB');
      return;
    }

    if (!selected.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setError('');
  };

  const removeFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const fileToBase64 = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!message.trim() || message.trim().length < 10) {
      setError('Description must be at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        type,
        name: name.trim(),
        message: message.trim(),
        pageUrl: window.location.pathname,
        timestamp: new Date().toISOString(),
      };

      if (file) {
        payload.screenshot = await fileToBase64(file);
      }

      await api.post('/api/feedback', payload);
      setSuccess(true);

      // Auto-close after 2s
      setTimeout(() => {
        setOpen(false);
        resetForm();
        setSuccess(false);
      }, 2000);

      // 30s cooldown
      setCooldown(true);
      setTimeout(() => setCooldown(false), 30000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to send. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* FAB Button */}
      <button
        onClick={handleOpen}
        disabled={cooldown}
        title={cooldown ? 'Please wait...' : 'Submit bug or feature request'}
        className={`fixed z-[60] bottom-6 right-6 sm:bottom-6 sm:right-6 h-12 px-4 rounded-full shadow-lg flex items-center justify-center gap-1.5 transition-all duration-200 ${
          cooldown
            ? 'bg-gray-400 cursor-not-allowed opacity-60'
            : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-105 active:scale-95 cursor-pointer'
        }`}
        style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}
      >
        <MessageSquare className="w-5 h-5 text-white" />
        <span className="text-white text-sm font-medium">Feedback</span>
      </button>

      {/* Modal */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[70]"
            onClick={handleClose}
          />

          {/* Modal Card */}
          <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div
              className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:rounded-xl shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 font-display">Submit Bug or Feature Request</h2>
                <button
                  onClick={handleClose}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Success State */}
              {success ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 gap-3">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-lg font-medium text-gray-900">Thanks! Submitted successfully.</p>
                  <p className="text-sm text-gray-500">We'll review it shortly.</p>
                </div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
                  {/* Type Toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setType('bug')}
                      className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        type === 'bug'
                          ? 'bg-red-50 text-red-700 ring-1 ring-red-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Bug Report
                    </button>
                    <button
                      type="button"
                      onClick={() => setType('feedback')}
                      className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        type === 'feedback'
                          ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Feature Request
                    </button>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your name"
                      maxLength={100}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={type === 'bug' ? 'What happened? What did you expect?' : 'Describe the feature you\'d like to see...'}
                      rows={4}
                      maxLength={2000}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 resize-none"
                    />
                    <div className="text-xs text-gray-400 text-right mt-0.5">
                      {message.length}/2000
                    </div>
                  </div>

                  {/* Screenshot */}
                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {!file ? (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors w-full"
                      >
                        <Paperclip className="w-4 h-4" />
                        Attach Screenshot
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                        <img
                          src={preview}
                          alt="Preview"
                          className="w-10 h-10 rounded object-cover border border-gray-200"
                        />
                        <span className="flex-1 text-sm text-gray-700 truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                          aria-label="Remove screenshot"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Error */}
                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>

                  {/* Helper text */}
                  <p className="text-xs text-gray-400 text-center">
                    Page URL auto-captured: {window.location.pathname}
                  </p>
                </form>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
